import React, { useContext } from 'react';
import PropTypes from 'prop-types';

import {
	MEMBERSHIP_TYPE_ADMIN,
	MEMBERSHIP_TYPE_OWNER,
} from '../constants/enums';
import useCurrentUser from '../hooks/useCurrentUser';
import useFilestack from '../hooks/useFilestack';
import useMyOrganization from '../hooks/useMyOrganization';
import types from '../utils/types';

const AppContext = React.createContext();

const propTypes = {

	/**
	 * The preloaded data required for SSR to render the context.
	 */
	preloadedData: PropTypes.shape({

		authState: PropTypes.string.isRequired,

		user: types.currentUser,

		organization: types.myOrganization.isRequired,

	}),

	/**
	 * The component children.
	 */
	children: PropTypes.node.isRequired,

};

/**
 * The app provider.
 *
 * @param {Object} props  The component props.
 *
 * @returns {Object} The provider component.
 */
const AppProvider = (props) => {

	const { preloadedData, children } = props;

	const {
		authState,
		currentUser,
		error: currentUserError,
		actions: userActions,
	} = useCurrentUser(preloadedData?.user, preloadedData?.authState);

	const {
		myOrganization,
		myOrganizationError,
	} = useMyOrganization(preloadedData?.organization);

	// Initialize the filestack settings and fetch an up-to-date upload policy
	const [filestack] = useFilestack(currentUser?.id, myOrganization?.id, {
		apiKey: FILESTACK_SETTINGS.API_KEY,
		container: FILESTACK_SETTINGS.CONTAINER,
		region: FILESTACK_SETTINGS.REGION,
		production: FILESTACK_SETTINGS.PRODUCTION,
	});

	// True, if the current user has admin privileges for the organization
	let isAdmin = false;

	// Find the membership in the current organization. myOrganization can
	// still be undefined here: it loads in parallel with currentUser, and when
	// the user fetch resolves first (e.g. the SSR org preload was missed) an
	// unguarded read crashes the whole app shell for logged-in users.
	const membership = currentUser?.memberships?.find(
		(temp) => temp.organization_id === myOrganization?.id
	);

	// Check to make sure that the current user has administrator privileges
	if (membership?.type === MEMBERSHIP_TYPE_ADMIN ||
		membership?.type === MEMBERSHIP_TYPE_OWNER) {
		isAdmin = true;
	}

	return (
		<AppContext.Provider
			value={{
				myOrganization,
				myOrganizationError,
				currentUser,
				currentUserError,
				authState,
				userActions,
				membership,
				isAdmin,
				filestack,
			}}>
			{children}
		</AppContext.Provider>
	);

};

AppProvider.propTypes = propTypes;

/**
 * The app context hook.
 *
 * @returns {Object} The shared context state.
 */
function useAppContext() {

	const context = useContext(AppContext);

	if (!context) {

		throw new Error('useAppContext must be used within a AppProvider');

	}

	return context;

}

export {
	AppProvider,
	useAppContext,
};
